import { Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { PupCreditService } from './pup-credit.service';

/**
 * A requested PUP Credit export, captured from the dialog when it closes.
 * The dialog only collects the request; this service runs it so the work
 * survives the dialog being destroyed.
 */
export type PupCreditExportRequest =
  | {
      kind: 'month';
      from: number;
      to: number;
      year: number;
      includeLockerTotalBookings: boolean;
      locationName: string | null;
      excludeClosed: boolean;
      sortByCountry: boolean;
      separateDailyMonthly: boolean;
    }
  | {
      kind: 'date';
      date: string;
      locationName: string | null;
      excludeClosed: boolean;
      sortByCountry: boolean;
    };

@Injectable({ providedIn: 'root' })
export class PupCreditExportService {
  // Dedicated toast channel; rendered by a custom template in app.layout.
  private readonly key = 'pupCredit';

  // Estimated 0-100 progress for the loading toast's determinate bar. The export
  // is a single request with no real % from the server, so we ease toward 90%
  // while it runs and snap to 100% the moment it finishes.
  readonly progress = signal(0);
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private pupCreditService: PupCreditService,
    private messageService: MessageService
  ) {}

  /**
   * Fires the export and drives a single sticky toast through
   * loading -> done (with link) / error. Sticky so it stays until dismissed.
   */
  run(request: PupCreditExportRequest) {
    const loadingTitle =
      request.kind === 'date' ? 'Exporting credit…' : 'Generating PUP Credit sheet…';

    this.startProgress();
    this.show({ state: 'loading', title: loadingTitle }, 'info');

    const request$ =
      request.kind === 'date'
        ? this.pupCreditService.exportCreditAtDate(
            request.date,
            request.locationName,
            request.excludeClosed,
            request.sortByCountry
          )
        : this.pupCreditService.generatePupCredit(
            request.from,
            request.to,
            request.year,
            request.includeLockerTotalBookings,
            request.locationName,
            request.excludeClosed,
            request.sortByCountry,
            request.separateDailyMonthly
          );

    request$.subscribe({
      next: (response: any) => {
        const url = response?.data?.url ?? '';
        if (url) {
          // Let the bar fill to 100% before swapping the toast to the link.
          this.finishProgress(() =>
            this.show({ state: 'done', title: 'Your sheet is ready', url }, 'success')
          );
        } else {
          this.stopProgress();
          this.show(
            { state: 'error', title: 'Something went wrong', message: 'Finished, but no link was returned.' },
            'error'
          );
        }
      },
      error: (error: any) => {
        this.stopProgress();
        const message =
          error?.status === 429
            ? error?.error?.error?.message || 'Rate limit exceeded. Try again shortly.'
            : error?.error?.error?.message || 'Unable to generate the sheet. Please try again.';
        this.show({ state: 'error', title: 'Export failed', message }, 'error');
      }
    });
  }

  private show(data: Record<string, unknown>, severity: 'info' | 'success' | 'error') {
    // Replace any prior state so only one PUP-credit toast is ever visible.
    this.messageService.clear(this.key);
    this.messageService.add({ key: this.key, severity, sticky: true, closable: true, data });
  }

  private startProgress() {
    this.stopProgress();
    this.progress.set(0);
    // Ease toward 90%: fast at first, slowing as it approaches, so it always
    // moves but never reaches 100% until the request actually completes.
    this.timer = setInterval(() => {
      const current = this.progress();
      if (current < 90) {
        const next = Math.min(90, current + Math.max(1, (90 - current) * 0.08));
        this.progress.set(Math.round(next));
      }
    }, 200);
  }

  private finishProgress(done: () => void) {
    this.stopProgress();
    this.progress.set(100);
    setTimeout(done, 350);
  }

  private stopProgress() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
